import { SelectedFileStatus } from '@vizit/atoms';
import { MAX_FILES_PER_UPLOAD } from '@vizit/constants';
import type * as React from 'react';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { UploadQueue } from '../upload-queue';
import {
  type UploadFunction,
  UploadQueueContext,
  type UploadQueueContextType,
  type UploadQueueFileInput,
  type UploadQueueItemData,
} from './upload-queue.context';
import { UploadQueueFullError } from './upload-queue.error';
import { uploadQueueReducer } from './upload-queue.state';

const AUTO_REMOVE_DELAY_MS = 8000;

interface UploadQueueProviderProps {
  /**
   * Child components
   * @type {React.ReactNode}
   */
  children?: React.ReactNode;
  /**
   * Maximum number of concurrent uploads
   * @type {number}
   * @default 5
   * @optional
   */
  maxConcurrent?: number;
}

/**
 * Provider component for managing upload queue state
 */
export function UploadQueueProvider(props: UploadQueueProviderProps) {
  const { children, maxConcurrent = 5 } = props;

  const [state, dispatch] = useReducer(uploadQueueReducer, { items: [], clearedCount: 0, clearedFailedCount: 0 });

  const activeUploadsRef = useRef<Set<string>>(new Set());
  const uploadFunctionsRef = useRef<Map<string, UploadFunction>>(new Map());

  const filesLeftCount = state.items.filter(
    (item) => item.status === SelectedFileStatus.Ready || item.status === SelectedFileStatus.Uploading,
  ).length;
  const hasUploadedFiles = state.items.some((item) => item.status === SelectedFileStatus.Success);

  const processQueue = useCallback(() => {
    const readyItems = state.items.filter(
      (item) => item.status === SelectedFileStatus.Ready && !activeUploadsRef.current.has(item.id),
    );

    const availableSlots = maxConcurrent - activeUploadsRef.current.size;
    const itemsToStart = readyItems.slice(0, availableSlots);

    if (itemsToStart.length === 0) return;

    // Mark items as uploading
    dispatch({ type: 'SET_UPLOADING', payload: { ids: itemsToStart.map((i) => i.id) } });

    for (const item of itemsToStart) {
      activeUploadsRef.current.add(item.id);
      const uploadFn = uploadFunctionsRef.current.get(item.id);

      // Start upload asynchronously
      (async () => {
        try {
          if (!uploadFn) {
            throw new Error('Upload function not found for item');
          }

          await uploadFn((progress) => {
            dispatch({ type: 'UPDATE_PROGRESS', payload: { id: item.id, progress } });
          });
          dispatch({ type: 'UPDATE_STATUS', payload: { id: item.id, status: SelectedFileStatus.Success } });
          uploadFunctionsRef.current.delete(item.id);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Upload failed';
          dispatch({
            type: 'UPDATE_STATUS',
            payload: { id: item.id, status: SelectedFileStatus.Error, error: errorMessage },
          });
          uploadFunctionsRef.current.delete(item.id);
        } finally {
          activeUploadsRef.current.delete(item.id);
        }
      })();
    }
  }, [state.items, maxConcurrent]);

  const handleOnAddFiles = useCallback(
    (files: UploadQueueFileInput[]): void => {
      // Only the files still to upload take room; finished ones leave the list as needed (see `MAX_LISTED_FILES`).
      const roomLeft = Math.max(MAX_FILES_PER_UPLOAD - filesLeftCount, 0);

      if (files.length > roomLeft) {
        throw new UploadQueueFullError(files.length, roomLeft);
      }

      const newItems: UploadQueueItemData[] = files.map((input) => {
        const id = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        // Store upload function in ref (can't store functions in reducer state)
        uploadFunctionsRef.current.set(id, input.onUpload);
        return {
          id,
          file: input.file,
          progress: 0,
          status: SelectedFileStatus.Ready,
          onUpload: input.onUpload,
        };
      });

      dispatch({ type: 'ADD_FILES', payload: newItems });
    },
    [filesLeftCount],
  );

  const handleOnRemoveItem = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { id } });
    activeUploadsRef.current.delete(id);
    uploadFunctionsRef.current.delete(id);
  }, []);

  const handleOnClearCompleted = useCallback(() => {
    dispatch({ type: 'CLEAR_COMPLETED' });
  }, []);

  const isUploading = useMemo(
    () => state.items.some((item) => item.status === SelectedFileStatus.Uploading),
    [state.items],
  );

  const contextValue = useMemo<UploadQueueContextType>(
    () => ({
      items: state.items,
      clearedCount: state.clearedCount,
      clearedFailedCount: state.clearedFailedCount,
      onAddFiles: handleOnAddFiles,
      onRemoveItem: handleOnRemoveItem,
      onClearCompleted: handleOnClearCompleted,
      isUploading,
    }),
    [
      state.items,
      state.clearedCount,
      state.clearedFailedCount,
      handleOnAddFiles,
      handleOnRemoveItem,
      handleOnClearCompleted,
      isUploading,
    ],
  );

  useEffect(() => {
    processQueue();
  }, [processQueue]);

  useEffect(() => {
    if (!isUploading) return;

    // Warn user before navigating away during uploads
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();

      return;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isUploading]);

  // Uploaded files leave the queue together, once nothing is left to upload, so the header counts every file of the
  // run ("Uploading 12 of 30") rather than only those not cleared yet. Files added meanwhile join the same count.
  useEffect(() => {
    if (filesLeftCount > 0 || !hasUploadedFiles) return;

    const timer = setTimeout(() => dispatch({ type: 'CLEAR_UPLOADED' }), AUTO_REMOVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [filesLeftCount, hasUploadedFiles]);

  return (
    <UploadQueueContext.Provider value={contextValue}>
      {children}
      <UploadQueue />
    </UploadQueueContext.Provider>
  );
}
